import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';
import { 
  Target, TrendingUp, TrendingDown, Users, DollarSign, MessageSquare, 
  Calendar, Award, Zap, ChevronRight, ArrowUp, ArrowDown, Layers,
  Lightbulb, CheckCircle, AlertTriangle, ArrowRightCircle, BarChart3
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ComposedChart, Line, Area
} from 'recharts';
import ExportButton from '../common/ExportButton';
import { exportToPDF, getTimestamp } from '../../utils/exportUtils';
import { PERSONA_COLORS } from '../../utils/personaColors';
import { PERSONA_INFO, COHORT_PROGRESSION } from '../../data/mockData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ideal cohorts (target state)
const IDEAL_COHORTS = ['Model Clients', 'Stable Performers', 'Premium Service'];
// Growth cohorts (can be improved)
const GROWTH_COHORTS = ['Scale Up', 'Optimize'];
// At-risk cohorts (need intervention)
const ATRISK_COHORTS = ['Turnaround', 'At Risk', 'Critical Review'];

const IdealClientProfile = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planGroups, setPlanGroups] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/plan-groups`);
        setPlanGroups(response.data.groups || []);
      } catch (err) {
        setError('Failed to load data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Calculate cohort analytics
  const cohortAnalytics = useMemo(() => {
    const analytics = {};
    
    // Initialize all cohorts
    Object.keys(PERSONA_INFO).forEach(cohort => {
      analytics[cohort] = {
        name: cohort,
        groups: [],
        count: 0,
        totalRevenue: 0,
        totalWorkload: 0,
        totalProfit: 0,
        avgRevenue: 0,
        avgWorkload: 0,
        avgMargin: 0,
        avgMeetings: 0,
        avgMessageCost: 0
      };
    });

    // Aggregate data by cohort
    planGroups.forEach(group => {
      const cohort = group.dominantPersona || 'Unknown';
      if (analytics[cohort]) {
        analytics[cohort].groups.push(group);
        analytics[cohort].count += 1;
        analytics[cohort].totalRevenue += group.totalRevenue;
        analytics[cohort].totalWorkload += group.totalWorkload;
        analytics[cohort].totalProfit += group.profit;
      }
    });

    // Calculate averages
    Object.keys(analytics).forEach(cohort => {
      const a = analytics[cohort];
      if (a.count > 0) {
        a.avgRevenue = a.totalRevenue / a.count;
        a.avgWorkload = a.totalWorkload / a.count;
        a.avgMargin = a.totalRevenue > 0 ? ((a.totalRevenue - a.totalWorkload) / a.totalRevenue) * 100 : 0;
        a.avgMeetings = a.groups.reduce((sum, g) => sum + (g.totalMeetings || 0), 0) / a.count;
        a.avgMessageCost = a.groups.reduce((sum, g) => sum + (g.totalMessageCost || 0), 0) / a.count;
      }
    });

    return analytics;
  }, [planGroups]);

  // Calculate ideal profile benchmarks from Model Clients
  const idealBenchmarks = useMemo(() => {
    const modelClients = cohortAnalytics['Model Clients'];
    if (!modelClients || modelClients.count === 0) return null;

    return {
      avgRevenue: modelClients.avgRevenue,
      avgWorkload: modelClients.avgWorkload,
      avgMargin: modelClients.avgMargin,
      avgMeetings: modelClients.avgMeetings,
      avgMessageCost: modelClients.avgMessageCost,
      maxWorkloadRatio: 0.30, // Workload should be <30% of revenue
      targetMargin: 50 // Target 50%+ margin
    };
  }, [cohortAnalytics]);

  // Calculate improvement opportunities
  const improvementOpportunities = useMemo(() => {
    const opportunities = [];

    // Analyze each non-ideal cohort
    [...GROWTH_COHORTS, ...ATRISK_COHORTS].forEach(cohortName => {
      const cohort = cohortAnalytics[cohortName];
      if (!cohort || cohort.count === 0) return;

      const progression = COHORT_PROGRESSION[cohortName];
      if (!progression) return;

      // Calculate potential impact
      const targetCohort = progression.target;
      const targetAnalytics = targetCohort ? cohortAnalytics[targetCohort] : idealBenchmarks;
      
      if (!targetAnalytics) return;

      const currentMargin = cohort.avgMargin;
      const targetMargin = targetCohort ? targetAnalytics.avgMargin : idealBenchmarks?.avgMargin || 50;
      const marginGap = targetMargin - currentMargin;
      
      // Estimate potential profit increase if moved to target cohort
      const potentialProfitIncrease = cohort.totalRevenue * (marginGap / 100);

      opportunities.push({
        cohort: cohortName,
        count: cohort.count,
        totalRevenue: cohort.totalRevenue,
        currentMargin,
        targetCohort: targetCohort || 'Model Clients',
        targetMargin,
        marginGap,
        potentialProfitIncrease,
        steps: progression.steps,
        priority: ATRISK_COHORTS.includes(cohortName) ? 'high' : 'medium',
        color: PERSONA_COLORS[cohortName]
      });
    });

    // Sort by potential profit increase
    return opportunities.sort((a, b) => b.potentialProfitIncrease - a.potentialProfitIncrease);
  }, [cohortAnalytics, idealBenchmarks]);

  // Summary statistics
  const summaryStats = useMemo(() => {
    const ideal = planGroups.filter(g => IDEAL_COHORTS.includes(g.dominantPersona));
    const growth = planGroups.filter(g => GROWTH_COHORTS.includes(g.dominantPersona));
    const atRisk = planGroups.filter(g => ATRISK_COHORTS.includes(g.dominantPersona));

    const idealRevenue = ideal.reduce((sum, g) => sum + g.totalRevenue, 0);
    const growthRevenue = growth.reduce((sum, g) => sum + g.totalRevenue, 0);
    const atRiskRevenue = atRisk.reduce((sum, g) => sum + g.totalRevenue, 0);
    const totalRevenue = idealRevenue + growthRevenue + atRiskRevenue;

    const totalPotentialImprovement = improvementOpportunities.reduce((sum, o) => sum + o.potentialProfitIncrease, 0);

    return {
      idealCount: ideal.length,
      growthCount: growth.length,
      atRiskCount: atRisk.length,
      totalGroups: planGroups.length,
      idealRevenue,
      growthRevenue,
      atRiskRevenue,
      totalRevenue,
      idealPct: planGroups.length > 0 ? (ideal.length / planGroups.length) * 100 : 0,
      totalPotentialImprovement
    };
  }, [planGroups, improvementOpportunities]);

  // Chart data for cohort comparison
  const cohortComparisonData = useMemo(() => {
    return Object.keys(PERSONA_INFO)
      .filter(k => k !== 'Unknown' && cohortAnalytics[k]?.count > 0)
      .map(cohort => ({
        name: PERSONA_INFO[cohort]?.short || cohort,
        fullName: cohort,
        groups: cohortAnalytics[cohort].count,
        revenue: cohortAnalytics[cohort].totalRevenue / 1000000,
        margin: cohortAnalytics[cohort].avgMargin,
        color: PERSONA_COLORS[cohort]
      }));
  }, [cohortAnalytics]);

  const handleExportPDF = async () => {
    await exportToPDF('ideal-client-profile-content', `cohort_analysis_${getTimestamp()}.pdf`, {
      backgroundColor: isDark ? '#0a0a0b' : '#f8fafc'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-6 rounded-xl border ${isDark ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" id="ideal-client-profile-content" data-testid="ideal-client-profile">
      {/* Hero Section */}
      <div className={`relative overflow-hidden rounded-2xl p-8 border transition-colors duration-300
        ${isDark 
          ? 'bg-gradient-to-br from-emerald-500/10 via-[#0d0d0f] to-blue-500/10 border-white/5' 
          : 'bg-gradient-to-br from-emerald-50 via-white to-blue-50 border-slate-200'}`}>
        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="relative">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                <Target className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Cohort Analysis & Optimization
                </h1>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Strategic insights to move OC groups into ideal categories
                </p>
              </div>
            </div>
            
            <ExportButton 
              onExportPDF={handleExportPDF}
              showExcel={false}
              pdfLabel="Export Report"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Ideal Groups */}
        <div className={`p-5 rounded-2xl border ${isDark ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200'}`}>
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle className="w-5 h-5 text-emerald-500" />
            <span className={`text-sm font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>Ideal Cohorts</span>
          </div>
          <p className={`text-3xl font-bold text-emerald-500`}>{summaryStats.idealCount}</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {summaryStats.idealPct.toFixed(0)}% of portfolio • ${(summaryStats.idealRevenue/1000000).toFixed(2)}M revenue
          </p>
        </div>

        {/* Growth Opportunity */}
        <div className={`p-5 rounded-2xl border ${isDark ? 'bg-cyan-500/5 border-cyan-500/20' : 'bg-cyan-50 border-cyan-200'}`}>
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="w-5 h-5 text-cyan-500" />
            <span className={`text-sm font-medium ${isDark ? 'text-cyan-400' : 'text-cyan-700'}`}>Growth Potential</span>
          </div>
          <p className={`text-3xl font-bold text-cyan-500`}>{summaryStats.growthCount}</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            Ready for optimization • ${(summaryStats.growthRevenue/1000000).toFixed(2)}M revenue
          </p>
        </div>

        {/* At Risk */}
        <div className={`p-5 rounded-2xl border ${isDark ? 'bg-red-500/5 border-red-500/20' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <span className={`text-sm font-medium ${isDark ? 'text-red-400' : 'text-red-700'}`}>Needs Intervention</span>
          </div>
          <p className={`text-3xl font-bold text-red-500`}>{summaryStats.atRiskCount}</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            Unprofitable groups • ${(summaryStats.atRiskRevenue/1000000).toFixed(2)}M at risk
          </p>
        </div>

        {/* Potential Improvement */}
        <div className={`p-5 rounded-2xl border ${isDark ? 'bg-purple-500/5 border-purple-500/20' : 'bg-purple-50 border-purple-200'}`}>
          <div className="flex items-center gap-3 mb-3">
            <Zap className="w-5 h-5 text-purple-500" />
            <span className={`text-sm font-medium ${isDark ? 'text-purple-400' : 'text-purple-700'}`}>Gross Profit Opportunity</span>
          </div>
          <p className={`text-3xl font-bold text-purple-500`}>+${(summaryStats.totalPotentialImprovement/1000000).toFixed(2)}M</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            If all groups reach target cohort
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['overview', 'benchmarks', 'opportunities', 'roadmap'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all capitalize
              ${activeTab === tab 
                ? 'bg-emerald-500 text-white' 
                : isDark 
                  ? 'bg-white/5 text-slate-400 hover:bg-white/10' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
          >
            {tab === 'overview' && <BarChart3 className="w-4 h-4 inline mr-1.5" />}
            {tab === 'benchmarks' && <Target className="w-4 h-4 inline mr-1.5" />}
            {tab === 'opportunities' && <Lightbulb className="w-4 h-4 inline mr-1.5" />}
            {tab === 'roadmap' && <ArrowRightCircle className="w-4 h-4 inline mr-1.5" />}
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cohort Distribution Chart */}
          <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200'}`}>
            <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Cohort Distribution by Revenue
            </h3>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cohortComparisonData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'} />
                  <XAxis type="number" tickFormatter={(v) => `$${v.toFixed(1)}M`} tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }} width={80} />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const d = payload[0].payload;
                        return (
                          <div className={`p-3 rounded-lg border ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
                            <p className={`font-bold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{d.fullName}</p>
                            <p className="text-xs text-emerald-500">{d.groups} groups • ${(d.revenue).toFixed(2)}M</p>
                            <p className={`text-xs ${d.margin >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                              {d.margin >= 0 ? '+' : ''}{d.margin.toFixed(1)}% gross margin
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                    {cohortComparisonData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Margin by Cohort */}
          <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200'}`}>
            <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Gross Margin Performance by Cohort
            </h3>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={cohortComparisonData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
                  <YAxis yAxisId="left" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }} />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => v} tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }} />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const d = payload[0].payload;
                        return (
                          <div className={`p-3 rounded-lg border ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
                            <p className={`font-bold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{d.fullName}</p>
                            <p className={`text-xs ${d.margin >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                              Gross Margin: {d.margin >= 0 ? '+' : ''}{d.margin.toFixed(1)}%
                            </p>
                            <p className="text-xs text-blue-500">{d.groups} groups</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar yAxisId="right" dataKey="groups" fill={isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'} radius={[4, 4, 0, 0]} />
                  <Line yAxisId="left" type="monotone" dataKey="margin" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', strokeWidth: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'benchmarks' && (
        <div className="space-y-6">
          {/* Ideal Client Benchmarks */}
          <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className={`p-2 rounded-lg ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                <Award className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Model Client Benchmarks
                </h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Based on {cohortAnalytics['Model Clients']?.count || 0} top-performing OC groups
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Avg Revenue</p>
                <p className={`text-2xl font-bold text-emerald-500`}>
                  ${((idealBenchmarks?.avgRevenue || 0) / 1000).toFixed(0)}k
                </p>
              </div>
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Target Margin</p>
                <p className={`text-2xl font-bold text-emerald-500`}>
                  +{idealBenchmarks?.avgMargin?.toFixed(0) || 50}%
                </p>
              </div>
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Max Workload Ratio</p>
                <p className={`text-2xl font-bold text-blue-500`}>
                  &lt;30%
                </p>
              </div>
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Avg Meetings/Year</p>
                <p className={`text-2xl font-bold text-purple-500`}>
                  {idealBenchmarks?.avgMeetings?.toFixed(1) || 0}
                </p>
              </div>
            </div>

            <div className={`p-4 rounded-xl ${isDark ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'}`}>
              <h4 className={`font-semibold text-sm mb-2 ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                Key Characteristics of Model Clients
              </h4>
              <ul className={`text-sm space-y-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Margin exceeds 50% - highly profitable relationships
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Workload cost is less than 30% of revenue - efficient operations
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Low meeting frequency - self-sufficient clients
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Moderate email volume - clear communication
                </li>
              </ul>
            </div>
          </div>

          {/* Cohort Comparison Table */}
          <div className={`p-6 rounded-2xl border overflow-hidden ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200'}`}>
            <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Cohort Comparison vs Benchmarks
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={isDark ? 'text-slate-500' : 'text-slate-400'}>
                  <tr>
                    <th className="text-left py-3 px-4 font-medium">Cohort</th>
                    <th className="text-center py-3 px-4 font-medium">Groups</th>
                    <th className="text-right py-3 px-4 font-medium">Avg Revenue</th>
                    <th className="text-right py-3 px-4 font-medium">Margin</th>
                    <th className="text-right py-3 px-4 font-medium">Gap to Ideal</th>
                    <th className="text-left py-3 px-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-slate-100'}`}>
                  {Object.keys(PERSONA_INFO)
                    .filter(k => k !== 'Unknown' && cohortAnalytics[k]?.count > 0)
                    .sort((a, b) => cohortAnalytics[b].avgMargin - cohortAnalytics[a].avgMargin)
                    .map(cohort => {
                      const data = cohortAnalytics[cohort];
                      const marginGap = (idealBenchmarks?.avgMargin || 87) - data.avgMargin;
                      const isIdeal = IDEAL_COHORTS.includes(cohort);
                      const isAtRisk = ATRISK_COHORTS.includes(cohort);
                      
                      return (
                        <tr key={cohort} className={isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-slate-50'}>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PERSONA_COLORS[cohort] }} />
                              <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{cohort}</span>
                            </div>
                          </td>
                          <td className={`py-3 px-4 text-center ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                            {data.count}
                          </td>
                          <td className={`py-3 px-4 text-right font-mono ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                            ${(data.avgRevenue / 1000).toFixed(0)}k
                          </td>
                          <td className={`py-3 px-4 text-right font-mono font-bold ${data.avgMargin >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                            {data.avgMargin >= 0 ? '+' : ''}{data.avgMargin.toFixed(1)}%
                          </td>
                          <td className={`py-3 px-4 text-right font-mono ${marginGap <= 0 ? 'text-emerald-500' : 'text-amber-500'}`}>
                            {marginGap <= 0 ? '—' : `-${marginGap.toFixed(0)}%`}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                              isIdeal 
                                ? 'bg-emerald-500/10 text-emerald-500' 
                                : isAtRisk 
                                  ? 'bg-red-500/10 text-red-500'
                                  : 'bg-amber-500/10 text-amber-500'
                            }`}>
                              {isIdeal ? 'Ideal' : isAtRisk ? 'At Risk' : 'Growth'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'opportunities' && (
        <div className="space-y-4">
          <div className={`p-4 rounded-xl ${isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-200'}`}>
            <p className={`text-sm ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
              <Lightbulb className="w-4 h-4 inline mr-2" />
              These opportunities are ranked by potential profit impact. Focus on high-priority items first.
            </p>
          </div>

          {improvementOpportunities.map((opp) => (
            <div 
              key={opp.cohort}
              className={`p-6 rounded-2xl border transition-colors ${isDark ? 'bg-[#111113] border-white/5 hover:border-white/10' : 'bg-white border-slate-200 hover:border-slate-300'}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: opp.color }} />
                  <div>
                    <h4 className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{opp.cohort}</h4>
                    <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      {opp.count} groups • ${(opp.totalRevenue/1000000).toFixed(2)}M revenue
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${
                    opp.priority === 'high' ? 'bg-red-500/10 text-red-500' : 'bg-amber-500/10 text-amber-500'
                  }`}>
                    {opp.priority === 'high' ? 'High Priority' : 'Medium Priority'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Current Margin</p>
                  <p className={`text-lg font-bold ${opp.currentMargin >= 0 ? 'text-amber-500' : 'text-red-500'}`}>
                    {opp.currentMargin >= 0 ? '+' : ''}{opp.currentMargin.toFixed(1)}%
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Target Cohort</p>
                  <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{opp.targetCohort}</p>
                </div>
                <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Target Margin</p>
                  <p className="text-lg font-bold text-emerald-500">+{opp.targetMargin.toFixed(1)}%</p>
                </div>
                <div className={`p-3 rounded-lg ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
                  <p className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Potential Profit</p>
                  <p className="text-lg font-bold text-emerald-500">+${(opp.potentialProfitIncrease/1000).toFixed(0)}k</p>
                </div>
              </div>

              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/[0.02]' : 'bg-slate-50'}`}>
                <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  RECOMMENDED ACTIONS
                </p>
                <div className="space-y-2">
                  {opp.steps.map((step) => (
                    <div key={step} className="flex items-start gap-2">
                      <ArrowRightCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`} />
                      <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'roadmap' && (
        <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200'}`}>
          <h3 className={`text-lg font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Cohort Progression Roadmap
          </h3>
          <p className={`text-sm mb-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Strategic pathway to move OC groups toward ideal categories
          </p>

          <div className="space-y-4">
            {/* Critical Review → At Risk */}
            <div className={`p-4 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center gap-4 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PERSONA_COLORS['Critical Review'] }} />
                  <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Critical Review</span>
                  <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>({cohortAnalytics['Critical Review']?.count || 0})</span>
                </div>
                <ArrowRightCircle className={`w-5 h-5 ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PERSONA_COLORS['At Risk'] }} />
                  <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>At Risk</span>
                </div>
                <ArrowRightCircle className={`w-5 h-5 ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PERSONA_COLORS['Scale Up'] }} />
                  <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Scale Up</span>
                </div>
              </div>
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Focus: Reduce engagement costs drastically, then build revenue
              </p>
            </div>

            {/* Turnaround → Optimize → Stable */}
            <div className={`p-4 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center gap-4 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PERSONA_COLORS['Turnaround'] }} />
                  <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Turnaround</span>
                  <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>({cohortAnalytics['Turnaround']?.count || 0})</span>
                </div>
                <ArrowRightCircle className={`w-5 h-5 ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PERSONA_COLORS['Optimize'] }} />
                  <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Optimize</span>
                </div>
                <ArrowRightCircle className={`w-5 h-5 ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PERSONA_COLORS['Stable Performers'] }} />
                  <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Stable Performers</span>
                </div>
              </div>
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Focus: Fee increases and service restructuring for high-revenue groups
              </p>
            </div>

            {/* Premium Service → Model Clients */}
            <div className={`p-4 rounded-xl border ${isDark ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200'}`}>
              <div className="flex items-center gap-4 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PERSONA_COLORS['Premium Service'] }} />
                  <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Premium Service</span>
                  <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>({cohortAnalytics['Premium Service']?.count || 0})</span>
                </div>
                <ArrowRightCircle className={`w-5 h-5 ${isDark ? 'text-emerald-600' : 'text-emerald-500'}`} />
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PERSONA_COLORS['Model Clients'] }} />
                  <span className={`font-semibold text-emerald-500`}>Model Clients</span>
                </div>
              </div>
              <p className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                Focus: Reduce engagement touchpoints while maintaining service quality
              </p>
            </div>
          </div>

          {/* Quick Wins Section */}
          <div className={`mt-6 p-4 rounded-xl ${isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-200'}`}>
            <h4 className={`font-semibold mb-3 ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
              <Zap className="w-4 h-4 inline mr-2" />
              Quick Wins for Maximum Impact
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  1. Address Turnaround Groups First
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {cohortAnalytics['Turnaround']?.count || 0} groups with ${((cohortAnalytics['Turnaround']?.totalRevenue || 0)/1000000).toFixed(2)}M revenue losing money. Fee conversations needed.
                </p>
              </div>
              <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  2. Optimize Premium Service
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {cohortAnalytics['Premium Service']?.count || 0} groups already profitable - small efficiency gains push them to Model status.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IdealClientProfile;
